import dayjs from 'dayjs';

/**
 * The operational period window an ICS form prints, in the same format the
 * export uses so the screen and the PDF cannot disagree.
 */
const OP_DISPLAY_FORMAT = 'MM/DD/YYYY hh:mm A';

export type OperationalPeriodWindow = {
  opFrom: string;
  opTo: string;
  /** Both bounds joined for the single-line boxes most widgets render. */
  range: string;
};

export type OperationalPeriodRow = {
  period_number?: number;
  start_date_time?: string;
  /**
   * Only written when the period is closed, so it is never the scheduled end of
   * a live period.
   */
  end_date_time?: string;
  anticipated_end_date_time?: string;
};

export function formatOperationalPeriodBound(
  value: string | Date | null | undefined
): string {
  if (value == null || value === '') return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(OP_DISPLAY_FORMAT) : '';
}

function windowOf(opFrom: string, opTo: string): OperationalPeriodWindow {
  return { opFrom, opTo, range: [opFrom, opTo].filter(Boolean).join(' to ') };
}

/**
 * Mirrors the three branches of the server's `resolveOperationalPeriodValues`,
 * which is what actually fills the operational period box on an exported form:
 *
 * - Planning the working period, where the server is sent no period number, uses
 *   the scheduled next-period window
 * - Period 0 runs from incident creation with no scheduled end
 * - Any started period uses its own row, taking the scheduled end rather than the
 *   actual one
 *
 * A started period with no row falls back to the scheduled window rather than
 * rendering blank.
 */
export function resolveOperationalPeriodWindow(args: {
  periodNumber: number;
  isWorking: boolean;
  periods: readonly OperationalPeriodRow[] | undefined;
  nextPeriodStart: string | Date | null | undefined;
  nextPeriodEnd: string | Date | null | undefined;
  incidentCreated: string | Date | null | undefined;
}): OperationalPeriodWindow {
  const scheduled = windowOf(
    formatOperationalPeriodBound(args.nextPeriodStart),
    formatOperationalPeriodBound(args.nextPeriodEnd)
  );

  if (args.isWorking) return scheduled;

  if (args.periodNumber === 0) {
    return windowOf(formatOperationalPeriodBound(args.incidentCreated), '');
  }

  const row = args.periods?.find(
    (period) => period.period_number === args.periodNumber
  );
  if (!row) return scheduled;

  return windowOf(
    formatOperationalPeriodBound(row.start_date_time),
    formatOperationalPeriodBound(row.anticipated_end_date_time)
  );
}
